<template>
  <custom-v-autocomplete
      v-bind="propsAutocomplete"
      @input="handleInput"
      @change="handleChange"
      @change-expanded-groups="handleChangeExpandedGroups"
      @change-expanded-group="handleChangeExpandedGroup"
      ref="autocomplete">
    <template #item="value" v-if="!!$scopedSlots['item']">
      <template v-if="value.item.loading === true">
        <v-progress-circular indeterminate style="margin: auto;"/>
      </template>
      <template v-else>
        <slot name="item" v-bind="value"></slot>
      </template>
    </template>
    <template #prepend><slot name="prepend"></slot></template>
    <template #prepend-inner><slot name="prepend-inner"></slot></template>
    <template #append><slot name="append"></slot></template>
    <template #append-outer><slot name="append-outer"></slot></template>
  </custom-v-autocomplete>
</template>

<script>
import Vue from "vue";
import {VAutocomplete, VExpansionPanels, VExpansionPanel, VExpansionPanelHeader, VExpansionPanelContent} from "vuetify/lib";
import VSelectList from "vuetify/lib/components/VSelect/VSelectList";
import natsort from "natsort";
import deepEquals from "fast-deep-equal";
import scrollIntoView from "scroll-into-view-if-needed";

/** ソート関数 */
const sortFn = natsort();

/** 「カテゴリなし」グループID */
const solesGroupId = '__soles__';

let nextPanelId = 1;

/** カスタム選択リスト */
const CustomVSelectList = Vue.extend(VSelectList).extend({
  props: {
    groups: {
      type: Array,
      required: false
    },
    itemGroup: {
      type: String,
      required: true
    },
    expandedGroups: {
      type: Object,
      required: true
    },
  },
  computed: {
    sortedGroups() {
      const groups = this.groups
          .slice()
          .sort((a, b) => {
            const s = sortFn(a.order ?? 0, b.order ?? 0);
            return s === 0 ? sortFn(a.name, b.name) : s;
          });
      groups.push({
        id: solesGroupId,
        name: 'カテゴリなし',
      });
      return groups;
    },
  },
  methods: {
    /**
     * カテゴリ毎に VExpansionPanel で開閉できるギミックを挟むレンダラー
     *
     * @return {*|VNode}
     */
    render() {
      if(this.groups) {
        // グループ毎
        const children = this.sortedGroups.map(group => {
          const items = this.items
              .filter(item => {
                const itemGroup = item[this.itemGroup] ?? [];
                if(group.id === solesGroupId) {
                  return itemGroup.length === 0;
                } else {
                  return itemGroup.includes(group.id);
                }
              })
              .filter(item => !this.hideSelected || !this.hasItem(item));
          return {items, ...group};
        });

        // 全てのグループが空の場合
        if(children.map(c => c.items.length > 0 ? 1 : 0).reduce((acc, val) => acc + val, 0) === 0) {
          children.push({vnode: this.$slots['no-data'] || this.staticNoDataTile});
        }

        return this.$createElement(VExpansionPanels, {
          staticClass: 'v-select-list v-model-select-list',
          class: this.themeClasses,
          attrs: {
            role: 'listbox',
            tabindex: -1,
          },
          props: {
            accordion: true,
            focusable: true,
            multiple: true,
            tile: true,
            value: this.sortedGroups
                .map((g, i) => this.expandedGroups[g.id] ? i : -1)
                .filter(i => i >= 0)
          },
          on: {
            change: this.handleChangeExpandedGroups
          }
        }, children.map(c => this.createVNode(c)));
      } else {
        return VSelectList.options.render.call(this);
      }
    },
    handleChangeExpandedGroups(value) {
      this.$emit('change-expanded-groups', this.sortedGroups
          .reduce((a, g, i) => { a[g.id] = value.includes(i); return a; }, {}));
    },
    createVNode(group) {
      let visible = true;
      const childNodes = [];
      if(group.name) {
        childNodes.push(this.$createElement(
            VExpansionPanelHeader, {}, group.name));
      }
      if(group.vnode) {
        childNodes.push(group.vnode);
      } else {
        childNodes.push(this.$createElement(
            VExpansionPanelContent, {}, group.items.map((item, index) =>
                this.genTile({item, index}))));
        visible = group.items.length > 0 || group.visible === true;
      }

      const panelId = `ExtensionPanel-${nextPanelId++}`;
      return this.$createElement(VExpansionPanel, {
        class: {
          'd-none': !visible
        },
        on: {
          'change': () => {
            const vm = this.$refs[panelId];
            this.$emit('change-expanded-group', {group, isActive: !vm.isActive});
          }
        },
        ref: panelId
      }, childNodes);
    },
  }
});
CustomVSelectList.options.render = CustomVSelectList.options.methods.render; // render override workaround

/** カスタムオートコンプリート */
const CustomVAutocomplete = Vue.extend(VAutocomplete).extend({
  props: {
    groups: {
      type: Array,
      required: false
    },
    itemGroup: {
      type: String,
      required: true
    },
    expandedGroups: {
      type: Object,
      required: true
    },
    taller: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    staticList() {
      if (this.$slots['no-data'] || this.$slots['prepend-item'] || this.$slots['append-item']) {
        console.error('assert: staticList should not be called if slots are used')
      }
      return this.$createElement(CustomVSelectList, this.listData)
    },
    listData() {
      const data = VAutocomplete.options.computed.listData.call(this);
      data.props = {
        ...data.props,
        groups: this.groups,
        itemGroup: this.itemGroup,
        expandedGroups: this.expandedGroups
      };
      data.on = {
        ...data.on,
        'change-expanded-groups': value => this.$emit('change-expanded-groups', value),
        'change-expanded-group': value => this.$emit('change-expanded-group', value),
      };
      return data;
    },
    virtualizedItems() {
      return this.computedItems;
    },
    $_menuProps() { // eslint-disable-line @typescript-eslint/camelcase
      const props = VAutocomplete.options.computed.$_menuProps.call(this);
      if(this.taller) {
        const innerHeight = this.getInnerHeight();
        const activator = this.$refs['input-slot'];
        if(activator) {
          const dimension = this.getRoundedBoundedClientRect(activator);
          props.maxHeight = Math.max(
              props.maxHeight,
              innerHeight - dimension.bottom - 20,
              dimension.bottom - 100);
        }
      }
      props.contentClass = `model-select__content ${props.contentClass || ''}`.trim();
      return props;
    }
  },
  methods: {
    getRoundedBoundedClientRect(el) {
      const rect = el.getBoundingClientRect()
      return {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        bottom: Math.round(rect.bottom),
        right: Math.round(rect.right),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      }
    },
    getInnerHeight() {
      if (!window) return 0;
      return window.innerHeight || document.documentElement.clientHeight;
    },
    /**
     * メニューが表示された直後にアクティブな選択肢までスクロールする
     *
     * @param val メニュー表示の場合は true 非表示の場合は false
     */
    onMenuActiveChange(val) {
      VAutocomplete.options.methods.onMenuActiveChange.call(this, val);

      if(val) {
        setTimeout(() => {
          const $content = this.$refs.menu?.$refs?.content;
          const $target = $content?.querySelector('div[aria-selected="true"]');
          if(!$target) {
            return;
          }
          scrollIntoView($target, {
            behavior: 'smooth',
            boundary: $content
          });
        }, 100);
      }
    },
  },
});


export default {
  name: 'ModelSelect',
  components: { CustomVAutocomplete },
  props: {
    ...VAutocomplete.options.props,
    /**
     * 選択肢を指定します。
     *
     * 以下を渡すことができます
     * - 配列
     * - 配列を返す Promise
     * - 配列を返す関数
     * - 配列を返す Promise を返す関数
     *
     * 配列の要素として以下の型のオブジェクトを指定します
     * {
     *   id: string | number | object;   // 値プロパティ
     *   name: string | number | object; // 表示文字列
     *   groups?: string[];              // 所属グループ
     *   disabled: boolean;              // 選択肢が無効の場合 true そうでない場合 false
     * }
     */
    items: {
      type: [Array, Promise, Function],
      required: true,
      default: () => [],
    },
    /**
     * 選択肢のグループを指定します。
     *
     * 以下を渡すことができます
     * - 配列
     * - 配列を返す Promise
     * - 配列を返す関数
     * - 配列を返す Promise を返す関数
     *
     * 配列の要素として以下の型のオブジェクトを指定します
     * {
     *   id: string;     // グループID
     *   name: string;   // グループ名
     *   order?: number; // 表示順序
     * }
     */
    groups: {
      type: [Array, Promise, Function],
      required: false,
    },
    /**
     * items で渡すオブジェクトの「所属グループ」プロパティ名を指定します
     * デフォルトは groups です。
     */
    itemGroup: {
      type: String,
      required: false,
      default: 'groups'
    },
    /**
     * items で渡すオブジェクトの「表示文字列」プロパティ名を指定します
     * デフォルトは name です。
     */
    itemText: {
      type: [String, Function],
      required: false,
      default: 'name'
    },
    /**
     * items で渡すオブジェクトの「値」プロパティ名を指定します
     * デフォルトは id です。
     */
    itemValue: {
      type: [String, Function],
      required: false,
      default: 'id'
    },
    /**
     * 選択肢をソートする関数を指定します。
     *
     * 関数の型は (a: any, b: any) => number
     */
    itemSort: {
      type: Function,
      required: false,
    },
    /**
     * グループ開閉状態を指定します。
     * グループIDをキー、開閉状態を表すブール値を値とするオブジェクトを指定します。
     */
    expandedGroups: {
      type: Object,
      required: false,
    },
    /** 初期状態でカテゴリをすべて展開した状態にしておく場合 true そうでない場合 false */
    defaultExpandAll: {
      type: Boolean,
      default: false,
    },
    /** ポップアップメニューの最大長を画面最下部まで延ばす場合 true そうでない場合 false */
    taller: {
      type: Boolean,
      default: false,
    },
    /** メニューの幅がアイテムの幅に従う */
    looseMenuWidth: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      viewItems: [],
      viewGroups: null,
      viewExpandedGroups: {},
      viewLoading: false
    };
  },
  computed: {
    propsAutocomplete() {
      const props = Object.entries(this.$options.propsData)
        .filter(([key]) => key !== 'value' && key !== 'items' && Object.prototype.hasOwnProperty.call(VAutocomplete.options.props, key))
        .reduce((acc, [key, val]) => { acc[key] = val; return acc; }, {});

      return Object.assign({
        value: this.viewValue,
        items: this.viewItems,
        itemText: this.itemText,
        itemValue: this.itemValue,
        returnObject: false,
        groups: this.viewGroups,
        itemGroup: this.itemGroup,
        expandedGroups: this.viewExpandedGroups,
        loading: this.viewLoading,
        taller: this.taller,
        menuProps: {
          contentClass: this.looseMenuWidth ? 'loose-menu-width' : 'tight-menu-width',
        },
      }, props);
    },
    itemValueFn() {
      return typeof this.itemValue === 'function' ?
          this.itemValue :
          item => item[this.itemValue];
    },
    itemTextFn() {
      return typeof this.itemText === 'function' ?
          this.itemText :
          item => item[this.itemText];
    },
    itemSortFn() {
      return typeof this.itemSort === 'function' ?
          this.itemSort :
          (a, b, textA, textB, sortFn) => sortFn(textA, textB)
    },
    viewValue() {
      if (Array.isArray(this.value)) {
        return this.items
            .find(item => this.value.includes(this.itemValueFn(item)))
            .sort((a, b) => this.itemSortFn(a, b, this.itemTextFn(a), this.itemTextFn(b), sortFn))
            .map(this.itemValueFn);
      }
      return this.value;
    },
  },
  methods: {
    updateViewItems(items) {
      const builder = items => {
        if(!Array.isArray(items)) {
          console.error('The given items must be an array.', items);
          return;
        }
        this.viewLoading = false;
        this.viewItems = items
            .slice()
            .sort((a, b) => this.itemSortFn(a, b, this.itemTextFn(a), this.itemTextFn(b), sortFn));
      };

      if(typeof items === 'function') {
        items = items();
      }
      if(typeof items ==='object' && typeof items.then === 'function') {
        items.then(builder);
      } else {
        builder(items);
      }
    },
    updateViewGroups(groups) {
      const builder = groups => {
        if(groups === undefined || groups === null) {
          this.viewGroups = null;
        } else if(!Array.isArray(groups)) {
          console.error('The given groups must be an array.', groups);
        } else {
          this.viewGroups = groups;
        }
        this.updateViewExpandedGroups(groups);
      };

      if(typeof groups === 'function') {
        groups = groups();
      }
      if(typeof groups ==='object' && typeof groups.then === 'function') {
        groups.then(builder);
      } else {
        builder(groups);
      }
    },
    updateViewExpandedGroups(groups) {
      if(groups === undefined) {
        groups = this.viewGroups;
      }
      if(groups) {
        const oldViewExpandedGroups = this.viewExpandedGroups ?? {};
        this.viewExpandedGroups = groups.reduce((a, g) => {
          a[g.id] = oldViewExpandedGroups[g.id] ?? this.defaultExpandAll;
          return a;
        }, {
          [solesGroupId]: true
        });
      } else {
        this.viewExpandedGroups = {};
      }
    },
    handleInput(value) {
      if (value === undefined) {
        value = null;
      }
      this.$emit('input', value);
    },
    handleChange(value) {
      if (value === undefined) {
        value = null;
      }
      /**
       * 最後の変更をキャンセルします
       */
      const cancel = () => {
        this.$refs.autocomplete.setValue(this.value);
      };
      this.$emit('change', value, {
        cancel
      });
    },
    handleChangeExpandedGroups(value) {
      if(deepEquals(this.viewExpandedGroups, value)) {
        return;
      }
      this.viewExpandedGroups = value;
      this.$emit('change-expanded-groups', value);
    },
    handleChangeExpandedGroup(value) {
      this.$emit('change-expanded-group', value);
    }
  },
  watch: {
    items: {
      immediate: true,
      handler(items) {
        this.updateViewItems(items);
      }
    },
    groups: {
      immediate: true,
      handler(groups) {
        this.updateViewGroups(groups);
      }
    },
    expandedGroups: {
      immediate: true,
      handler(value) {
        this.viewExpandedGroups = value ?? this.viewExpandedGroups ?? {};
        this.updateViewExpandedGroups();
      }
    },
    loading(value) {
      this.viewLoading = value;
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-expansion-panel-content__wrap {
    padding: 0;
  }

  .v-expansion-panel-header {
    font-size: 80%;
    color: rgba(0, 0, 0, 0.6);
    padding-top: 15px;
    padding-bottom: 15px;
    min-height: auto;
  }
}
</style>

<style lang="scss">
.v-model-select-list .v-expansion-panel-header {
  white-space: nowrap;
  overflow: hidden;
}

.model-select__content {
  &.tight-menu-width {
    max-width: min-content;
  }

  &.loose-menu-width {
    max-width: 80%;
  }
}


</style>
